import { createApp } from 'vue/dist/vue.esm-bundler';
import App from './App.vue';
import router from './router';
import { IonicVue } from '@ionic/vue';
import { isIonicSwipeBackEnabled } from '@/utils/android-back-navigation';
import MasterLayout from './components/MasterLayout.vue';
import EmptyLayout from './components/EmptyLayout.vue';
import AbsolutelyLayout from './components/AbsolutelyLayout.vue';

import store from './store';

// Import custom Capacitor plugins to ensure they're registered
import { auth0, getAccessTokenSilentlyWithMonitoring } from './utils/auth0';
import {
  launchBudget,
  LAUNCH_BUDGET_SPENT,
  hasStoredAuth0Session,
} from './utils/launch-auth-budget';
import HtmlToVue from './components/common/HtmlToVue.vue';
import InfiniteLoading from 'v3-infinite-loading';
import 'v3-infinite-loading/lib/style.css'; //required if you're not going to override default slots
import VueCookies from 'vue3-cookies';
import VueClickAway from 'vue3-click-away';
import VueTelInput from 'vue-tel-input';
import Vueform from '@vueform/vueform';
import vueformConfig from './../vueform.config';
import {
  initDatadogLogs,
  initDatadogRum,
  addDatadogRumError,
  clearDatadogRumUser,
  getDatadogRumStatus,
} from '@/utils/datadog';

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';
import 'vue-tel-input/vue-tel-input.css';

/* Theme variables */
import './theme/variables.css';

/* Icon + touch-target sizing tokens (phone -> tablet step lives here, once) */
import './theme/control-sizing.css';

/* Dynamic font scaling — defines the rem base every text size hangs off */
import './theme/font-scale.css';

/* Next Steps lesson type scale (phone -> tablet rung), from the printed styleguide */
import './theme/next-steps-type.css';

// What `.scanner-active` does. Global on purpose: two screens scan now, and
// the rules used to be global only because photodrop happened to be a static
// import. See the file.
import './theme/scanner-transparency.css';

/* HOGC One Design System */
import '@team-hogc/hogc-one-design-system/style.css';

/* TailwindCSS */
import './styles/main.scss';
import 'tailwindcss/base.css';

/* Mock API Error for testing */
import { initMockApiError } from './utils/interceptors/mock-api-error';
/* Mock Timeout API for testing */
import { initMockTimeoutApi } from './utils/interceptors/mock-timeout-api';
/* 401 Error Interceptor */
import {
  init401ErrorInterceptor,
  safeLogoutAfterUnrecoverable401,
} from './utils/interceptors/401-error';
/* Fix B — Authorization header guard interceptor */
import { initAuthHeaderInterceptor } from './utils/interceptors/auth-header';
import { initGivingPinSessionInterceptor } from './utils/interceptors/giving-pin-session';
import { initGivingLoginInterceptor } from './utils/interceptors/giving-login';
import {
  installAnnotationReadPath,
  rotationTargetFromStore,
  keyMaterialFromStore,
} from './utils/courses-annotations-read-path';
import 'tailwindcss/components.css';
import 'tailwindcss/utilities.css';
import { initializeNotification, requestNotificationPermission } from '@/utils/notification';
import {
  isInStoryblokVisualEditor,
  loadStoryblokBridge,
  initStoryblokBridge,
  getStoryblokStoryIdFromUrl,
} from '@/utils/storyblok-visual-editor';
import { isStoryblokPreviewMode } from '@/utils/storyblok';
import { preloadScannerSuccessAnimation } from '@/utils/scanner-success-animation';
import { notifyOtaAppReady, reapStaleOtaBundles } from '@/utils/ota';
import { bootFontScale } from '@/composables/useFontScale';
import { startSigningCheck } from '@/utils/rasp';

// Anti-tamper (VAPT 2026-10-07): is this binary signed with our Play certificate /
// Apple Team ID? First, so a repackaged copy is caught before member data loads.
// Not awaited and never throws; a no-op on web and on store binaries that predate
// the native plugin (OTA bundles run there too). Policy: src/utils/rasp/signing-policy.js.
startSigningCheck();

// Initialize mock API error (must be called before axios requests)
initMockApiError();
// Initialize mock timeout API (must be called before axios requests)
initMockTimeoutApi();
// Fix B — install request interceptor BEFORE response interceptor so every
// outgoing request gets the Bearer guard applied.
initAuthHeaderInterceptor();
// Initialize 401 error interceptor (must be called after auth0 is initialized)
init401ErrorInterceptor();
// MyGiving PIN step-up: X-Giving-Pin-Session on giving-api calls, and back to the
// PIN screen when giving-api answers 403 PIN_REQUIRED.
initGivingPinSessionInterceptor({ router });
// Giving per login: a login that has not confirmed the personal id is sent from a
// giving-record page to the MyGiving check when giving-api refuses it.
initGivingLoginInterceptor({ router });
// Open encrypted Next Steps annotations as they arrive from the API, under the
// key the courses-keys store holds for the signed-in member (hydrated in
// App.vue; a row that lands first waits for that hydration). A sealed row this device cannot open is marked, not dropped, and the
// screens say why — including on a build that predates a newer envelope
// version, which is what spec section 6.7 ("Old clients") asks of it.
installAnnotationReadPath({
  keyMaterial: keyMaterialFromStore(store),
  rotationTarget: rotationTargetFromStore(store),
});

// Warm the scanner success animation at app startup so the first scan does not
// wait for the Rive runtime and success asset after check-in completes.
preloadScannerSuccessAnimation();

const app = createApp(App)
  .use(IonicVue, {
    // iOS-mode swipe-back is a JS gesture. On Android the same left-edge swipe
    // is also the system back (ONE-8286). Keep the gesture on iOS/web only.
    swipeBackEnabled: isIonicSwipeBackEnabled(),
    // Force iOS mode for consistent animations across all platforms
    mode: 'ios',
  })
  .use(router)
  .use(auth0)
  .use(store)
  .use(VueCookies)
  .use(VueTelInput)
  .use(Vueform, vueformConfig)

  .component('master-layout', MasterLayout)
  .component('empty-layout', EmptyLayout)
  .component('absolutely-layout', AbsolutelyLayout)
  .component('html-to-vue', HtmlToVue)
  .component('infinite-loading', InfiniteLoading);

// Initialize Datadog Logs and RUM immediately to capture splash screen and login process
// This must happen before router.isReady() to ensure session recording starts from the beginning
// Wrap in try-catch and use setTimeout to make it non-blocking - errors won't prevent app from loading
setTimeout(async () => {
  try {
    initDatadogLogs();
    await initDatadogRum();

    // Expose diagnostic function to window for debugging
    if (typeof window !== 'undefined' && process.env.VUE_APP_NODE_ENV !== 'production') {
      window.getDatadogRumStatus = getDatadogRumStatus;
      console.log('[Datadog] Diagnostic function available: window.getDatadogRumStatus()');
    }

    // Fix-* debug helper: dump the state the fixes care about. Safe on prod
    // because it only reads localStorage + store state; no side effects.
    if (typeof window !== 'undefined') {
      window.hogcFixDebug = async () => {
        const { readCachedProfile, LAST_MEMBER_ID_KEY, LAST_PROFILE_SNAPSHOT_KEY } = await import(
          '@/utils/mixpanel'
        );
        const cached = readCachedProfile();
        const snapshot = {
          cachedMemberId: localStorage.getItem(LAST_MEMBER_ID_KEY),
          cachedProfile: cached?.snapshot || null,
          aliasedUsers: JSON.parse(localStorage.getItem('mixpanel_aliased_users') || '[]'),
          storeToken: store.state?.user?.token ? '(present)' : '(empty)',
          auth0IsAuthenticated: auth0?.isAuthenticated?.value ?? 'unknown',
          lastProfileSnapshotRaw: localStorage.getItem(LAST_PROFILE_SNAPSHOT_KEY),
        };
        console.table(snapshot);
        return snapshot;
      };
      console.log('[Fix-* Debug] Run window.hogcFixDebug() to inspect state');
    }
  } catch (error) {
    // Silently fail - don't block app initialization if Datadog fails
    console.error('[Datadog] Initialization failed - app will continue to load:', error);
  }
}, 0);

// Initialize Storyblok Visual Editor Bridge if in visual editor mode
// Use setTimeout to ensure window is fully ready
setTimeout(() => {
  if (typeof window !== 'undefined') {
    console.log('🔍 [Storyblok] Main.js - Checking for visual editor...');
    const isVisualEditor = isInStoryblokVisualEditor();
    console.log('🔍 [Storyblok] Main.js - Visual editor check result:', isVisualEditor);

    if (isVisualEditor) {
      console.log('═══════════════════════════════════════════════════════');
      console.log('🎯 [Storyblok] Initializing Visual Editor Bridge (from main.js)');
      console.log('═══════════════════════════════════════════════════════');
      console.log('📍 URL:', window.location.href);
      console.log('⏰ Timestamp:', new Date().toISOString());
      console.log('═══════════════════════════════════════════════════════');

      loadStoryblokBridge()
        .then(() => {
          console.log('✅ [Storyblok] Bridge loaded successfully');
          // Extract story ID from URL parameters
          const storyId = getStoryblokStoryIdFromUrl();
          console.log('📖 Story ID from URL:', storyId);
          // Initialize bridge after a short delay to ensure it's ready
          setTimeout(() => {
            console.log('🚀 [Storyblok] Calling initStoryblokBridge...');
            initStoryblokBridge(storyId);
          }, 100);
        })
        .catch(error => {
          console.error('═══════════════════════════════════════════════════════');
          console.error('❌ [Storyblok] Failed to initialize visual editor bridge');
          console.error('═══════════════════════════════════════════════════════');
          console.error('Error:', error);
          console.error('Error message:', error?.message);
          console.error('Error stack:', error?.stack);
          console.error('═══════════════════════════════════════════════════════');
        });
    } else {
      console.log('ℹ️  [Storyblok] Not in visual editor mode, skipping bridge initialization');
    }
  }
}, 0);

// Load the stored reader text size. Applying it is the router's job — it only
// takes effect on Next Steps routes (see applyScaleForRoute in the router).
bootFontScale();

router.isReady().then(async () => {
  // OTA: tell Capgo the bundle is healthy.
  //
  // Placement matters. There are three mount paths below (local, preview and
  // the Auth0 one), and the Auth0 path mounts in a `.finally()` *after* a
  // network call. Notifying from there would tie bundle health to Auth0
  // latency: a slow connection would blow Capgo's ready timeout and revert a
  // perfectly good bundle. Calling it here covers all three paths with one
  // call, and asserts what rollback is actually meant to detect — the bundle
  // parsed, Vue was constructed and the router resolved.
  //
  // Not awaited: this must never delay the mount.
  notifyOtaAppReady();

  // Clear out any bundle record a killed download left behind. It cannot help
  // the check that already ran — the plugin checks at process start, before any
  // JS exists — but it unblocks the next one, which arrives on the following
  // background or launch. Also not awaited, and it swallows its own failures.
  reapStaleOtaBundles();

  app.use(VueClickAway);

  // Check if we're in preview mode - skip Auth0 for faster loading
  const isPreview =
    typeof window !== 'undefined' &&
    (window.location.pathname.startsWith('/preview/') || isStoryblokPreviewMode());

  if (process.env.VUE_APP_NODE_ENV === 'local') {
    const token = localStorage.getItem('token');
    if (token) store.commit('user/setToken', token);
    app.mount('#app');
    // Initialize bridge after mount if in visual editor
    if (isInStoryblokVisualEditor()) {
      setTimeout(() => {
        const storyId = getStoryblokStoryIdFromUrl();
        initStoryblokBridge(storyId);
      }, 500);
    }
    return;
  }

  // Skip Auth0 authentication for preview mode - mount immediately
  if (isPreview) {
    app.mount('#app');
    // Skip notification initialization for preview mode
    return;
  }

  // Fix C — identify Mixpanel/OneSignal from the cached profile snapshot
  // BEFORE Auth0 is attempted. Decouples analytics identity from Auth0 health.
  try {
    const { identifyFromCache } = await import('@/utils/mixpanel');
    const identified = identifyFromCache();
    console.log('[Fix-C Launch] Cached identify attempted:', identified);
  } catch (e) {
    console.warn('[Fix-C Launch] identifyFromCache failed:', e);
  }

  // cacheMode 'on' reads cache then performs a silent refresh via the
  // refresh-token grant. 'cache-only' (an earlier value) was forbidden from
  // network refresh, so any missing refresh token surfaced as terminal. Only
  // 'on' | 'off' | 'cache-only' are valid per @auth0/auth0-spa-js.
  //
  // Ask only when this device actually holds a session. A device with no Auth0
  // cache entry has nothing to refresh, so the call is a guaranteed
  // missing_refresh_token — which the catch below logged at ERROR level on
  // every launch of every signed-out device, forever. That was 12,838 errors
  // across 3,017 sessions and 0 identified users in 30 days: not members being
  // logged out, just the expected answer reported as a fault
  // (hogc-one-mobile#1981). Everyone else mounts to pre-sign-in, where the
  // Sign In button runs the interactive login.
  const launchToken = (
    hasStoredAuth0Session()
      ? getAccessTokenSilentlyWithMonitoring({ cacheMode: 'on' }, 'app-initialization')
      : Promise.resolve(undefined)
  )
    .then(token => {
      if (!token) return;
      store.commit('user/setToken', token);
      console.log('[Fix-A Auth0] Token acquired at launch, length:', token?.length || 0);
    })
    .catch(async err => {
      const errCode = err?.error || err?.message;
      console.error('[Fix-A Auth0] Token acquisition failed at launch:', errCode, err);
      // Fix A — treat missing_refresh_token as a session-ended signal, same as
      // login_required/invalid_grant. Previously it was swallowed, leaving the
      // app mounted with an empty user.token and every API call returning 401.
      // Only force the logout flow when the user actually had a session. On a
      // fresh install (or after localStorage was cleared) missing_refresh_token /
      // login_required are expected — calling auth0.logout there would redirect
      // back through the deep-link handler and re-enter this same path,
      // producing an infinite re-login loop. The router-guard already follows
      // this pattern (src/router/index.js:764).
      const terminalErrors = ['login_required', 'invalid_grant', 'missing_refresh_token'];
      const wasAuthenticated = auth0?.isAuthenticated?.value === true;
      if (wasAuthenticated && terminalErrors.includes(errCode)) {
        console.warn(
          '[Fix-A Auth0] Terminal auth error for authenticated user, clearing sessions and logging out:',
          errCode,
        );
        // The shared logout, so this and the 401 interceptor (the launch
        // prefetches 401 at the same moment) present one browser, not two.
        await safeLogoutAfterUnrecoverable401('launch-logout');
      } else if (terminalErrors.includes(errCode)) {
        console.log(
          '[Fix-A Auth0] No prior session — skipping logout, app will mount to pre-sign-in:',
          errCode,
        );
      }
    });

  // Mount when the token call settles OR the launch budget runs out, whichever
  // is first. The call used to gate the mount outright, and a stalled refresh to
  // Auth0 (~30 s: 10 s x 3 attempts in auth0-spa-js) kept the app blank for that
  // long — members force-closed and relaunched until one got through. The call
  // keeps running past the budget: the `.then` above still commits the token and
  // the `.catch` still handles a session that has ended. See
  // src/utils/launch-auth-budget.js.
  let mounted = false;
  const mountApp = () => {
    if (mounted) return;
    mounted = true;
    app.mount('#app');
    initializeNotification();
    // Initialize bridge after mount if in visual editor
    if (isInStoryblokVisualEditor()) {
      setTimeout(() => {
        const storyId = getStoryblokStoryIdFromUrl();
        initStoryblokBridge(storyId);
      }, 500);
    }
  };

  launchBudget
    .race(launchToken)
    .then(outcome => {
      if (outcome !== LAUNCH_BUDGET_SPENT) return;
      // A session that turns out to have ended is still handled: the `.catch`
      // above logs out on a terminal error once the late call settles. It reads
      // `isAuthenticated`, which reflects the cached ID token, so it is true for
      // exactly the returning member the router admitted early.
      console.warn(
        '[Launch] Auth0 has not answered within the launch budget — showing the app now',
      );
    })
    // launchToken's own .catch can still reject: its logout path awaits a dynamic
    // import, which fails when the device is offline.
    .catch(error => console.warn('[Launch] Token wait failed:', error?.message || error))
    .finally(mountApp);
});

app.config.errorHandler = async function (err, vm, info) {
  if (process.env.VUE_APP_NODE_ENV !== 'production') {
    console.log('in error handler');
    console.error(err);
  }

  // Send error to Datadog RUM
  addDatadogRumError(err, {
    vueInfo: info,
    componentName: vm?.$options?.name || 'Unknown',
  });

  // Same guard and shared logout as unhandledrejection below (#1981, #1965).
  if (
    err.message === 'Request failed with status code 401' &&
    auth0?.isAuthenticated?.value === true
  ) {
    await safeLogoutAfterUnrecoverable401('error-handler-logout');
  }
};

window.onerror = function (message, source, lineno, colno, error) {
  if (process.env.VUE_APP_NODE_ENV !== 'production') {
    console.log('in window error handler');
    console.log(message);
    console.error(error);
  }
  // Send error to Datadog RUM
  addDatadogRumError(error || new Error(message), {
    source,
    lineno,
    colno,
  });
};

window.addEventListener('unhandledrejection', async event => {
  if (process.env.VUE_APP_NODE_ENV !== 'production') {
    console.log('in unhandled rejecction');
    console.error(event);
    console.log(event.reason.message);
  }
  // Only act when there WAS a session. auth0.logout() clears the local token
  // cache, so running it for someone who never signed in guarantees
  // missing_refresh_token on every later launch and bounces them to the Auth0
  // logout page for nothing — a state that sustains itself (#1981). The handled
  // path above already guards this way; this handler predates it and did not.
  if (
    event.reason &&
    event.reason.message &&
    auth0?.isAuthenticated?.value === true &&
    (event.reason.message === 'Request failed with status code 401' ||
      event.reason.message === 'Login required' ||
      event.reason.message.startsWith('Missing Refresh Token') ||
      event.reason.message.startsWith('Unknown or invalid refresh token'))
  ) {
    // The shared logout. The 401 interceptor now rejects with the original 401,
    // so an uncaught one lands here while the interceptor's own logout may still
    // be presenting — sharing its in-flight promise keeps that to one browser.
    await safeLogoutAfterUnrecoverable401('unhandled-rejection-logout');
  }
  // Send error to Datadog RUM
  addDatadogRumError(event.reason || new Error('Unhandled promise rejection'), {
    reason: event.reason?.message || 'Unknown',
  });
});
